import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/shared/lib"

/*
 * Verdant badges are mono, 22px tall, radius 6. They label a value — a plan, a
 * count, an id fragment. Anything that reports *state* is a StatusBadge (dot +
 * label) instead; the semantic variants here exist for plate-shaped labels like
 * a severity tag, not as a shortcut around that rule.
 */
const badgeVariants = cva(
  "group/badge inline-flex h-5.5 w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden rounded-full border border-transparent px-2 font-mono text-label whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-primary/13 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&>svg]:pointer-events-none [&>svg]:size-icon-xs!",
  {
    variants: {
      variant: {
        default:
          "bg-accent-soft font-medium text-accent-text [a]:hover:bg-accent-soft/70",
        secondary: "bg-secondary text-fg-secondary [a]:hover:bg-surface-4",
        destructive:
          "border-status-danger-bd bg-status-danger-bg font-medium text-destructive-text focus-visible:ring-destructive/20",
        success:
          "border-status-success-bd bg-status-success-bg font-medium text-success-text",
        warning:
          "border-status-warning-bd bg-status-warning-bg font-medium text-warning-text",
        info: "border-status-info-bd bg-status-info-bg font-medium text-info-text",
        outline: "border-border text-fg-secondary [a]:hover:bg-surface-2",
        ghost: "text-fg-secondary hover:bg-surface-2 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "secondary",
    },
  }
)

function Badge({
  className,
  variant = "secondary",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export { Badge, badgeVariants }
